import { useState, useEffect } from 'react';
import { FaSearch, FaMapMarkerAlt, FaClock, FaArrowRight, FaCheckCircle } from 'react-icons/fa';
import api from '../api';

const categories = ['C#', 'Java', 'SQL Server', 'React', 'Angular', 'Oracle DBA', 'Power BI', 'Azure'];

const states = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
  'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
  'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
  'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
  'Wisconsin','Wyoming',
];

const typeColors = {
  'Contract': 'bg-amber-100 text-amber-700',
  'Contract-to-Hire': 'bg-violet-100 text-violet-700',
  'Full-time': 'bg-emerald-100 text-emerald-700',
};

const remoteColors = {
  'Remote': 'bg-cyan-100 text-cyan-700',
  'Hybrid': 'bg-blue-100 text-blue-700',
  'Onsite': 'bg-gray-100 text-gray-600',
};

export default function Careers() {
  const [allPositions, setAllPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [category, setCategory] = useState('');
  const [state, setState] = useState('');
  const [searched, setSearched] = useState(false);
  const [results, setResults] = useState([]);

  useEffect(() => {
    api.getPositions()
      .then(data => {
        setAllPositions(data.map(p => ({ ...p, tech: Array.isArray(p.tech) ? p.tech : JSON.parse(p.tech || '[]') })));
      })
      .catch(() => setAllPositions([]))
      .finally(() => setLoading(false));
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const filtered = allPositions.filter(p => {
      const matchesKeyword = !keyword ||
        p.title.toLowerCase().includes(keyword.toLowerCase()) ||
        p.tech.some(t => t.toLowerCase().includes(keyword.toLowerCase())) ||
        p.description.toLowerCase().includes(keyword.toLowerCase());
      const matchesCategory = !category || p.category === category || p.tech.some(t => t.includes(category));
      const matchesState = !state || p.location.includes(state) || p.remote === 'Remote';
      return matchesKeyword && matchesCategory && matchesState;
    });
    setResults(filtered);
    setSearched(true);
  };

  const clearSearch = () => {
    setKeyword('');
    setCategory('');
    setState('');
    setSearched(false);
    setResults([]);
  };

  const displayPositions = searched ? results : allPositions;

  return (
    <div className="bg-white">

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0c1b4d] to-[#1d4ed8] text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
        <div className="relative max-w-7xl mx-auto px-4">
          <span className="text-cyan-400 text-sm font-semibold uppercase tracking-widest">Join Our Network</span>
          <h1 className="text-4xl md:text-5xl font-bold mt-2 mb-4">Open Positions</h1>
          <p className="text-blue-200 max-w-2xl text-lg leading-relaxed">
            We connect talented IT professionals with top clients across the US. Browse our current openings and take the next step in your career.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 40" className="w-full fill-white" preserveAspectRatio="none">
            <path d="M0,40 C480,0 960,40 1440,0 L1440,40 Z" />
          </svg>
        </div>
      </section>

      {/* Search */}
      <section className="py-10 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <form onSubmit={handleSearch}>
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1 relative">
                <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                <input
                  type="text"
                  value={keyword}
                  onChange={e => setKeyword(e.target.value)}
                  placeholder="Search by title, technology..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="md:w-52 px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-600"
              >
                <option value="">All Technologies</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select
                value={state}
                onChange={e => setState(e.target.value)}
                className="md:w-48 px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-600"
              >
                <option value="">All States</option>
                {states.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <button
                type="submit"
                className="px-6 py-3 bg-[#0c1b4d] text-white font-semibold rounded-xl hover:bg-blue-900 transition-colors flex items-center gap-2 text-sm"
              >
                <FaSearch size={13} /> Search
              </button>
              {searched && (
                <button type="button" onClick={clearSearch} className="px-4 py-3 border border-gray-200 text-gray-500 rounded-xl hover:bg-slate-50 text-sm">
                  Clear
                </button>
              )}
            </div>
          </form>
        </div>
      </section>

      {/* Results */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-gray-500">
              {loading ? 'Loading positions...' : searched
                ? results.length > 0 ? `${results.length} position${results.length !== 1 ? 's' : ''} found` : 'No matching positions found'
                : `${allPositions.length} open positions`}
            </p>
          </div>

          {searched && results.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
              <div className="text-gray-300 text-5xl mb-4">🔍</div>
              <h3 className="text-lg font-semibold text-gray-600 mb-2">No positions found</h3>
              <p className="text-gray-400 text-sm mb-4">Try adjusting your search criteria or clearing the filters.</p>
              <button onClick={clearSearch} className="px-4 py-2 bg-[#0c1b4d] text-white text-sm font-medium rounded-lg hover:bg-blue-900 transition-colors">
                View All Positions
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {displayPositions.map(pos => (
                <div key={pos.id} className="bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-lg transition-all hover:-translate-y-0.5">
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${typeColors[pos.type]}`}>{pos.type}</span>
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${remoteColors[pos.remote]}`}>{pos.remote}</span>
                  </div>
                  <h3 className="text-lg font-bold text-[#0c1b4d] mb-1">{pos.title}</h3>
                  <div className="flex flex-wrap gap-3 text-xs text-gray-400 mb-3">
                    <span className="flex items-center gap-1"><FaMapMarkerAlt size={11} /> {pos.location}</span>
                    <span className="flex items-center gap-1"><FaClock size={11} /> {pos.duration}</span>
                    <span>Posted {pos.posted}</span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{pos.description}</p>
                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {pos.tech.map(t => (
                      <span key={t} className="px-2.5 py-1 bg-slate-100 text-gray-600 text-xs rounded-full font-medium">{t}</span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <a
                      href="mailto:hr@marvyitservices.com"
                      className="flex-1 text-center py-2.5 bg-[#0c1b4d] text-white text-sm font-semibold rounded-xl hover:bg-blue-900 transition-colors"
                    >
                      Apply Now
                    </a>
                    <a
                      href="mailto:hr@marvyitservices.com"
                      className="px-4 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-slate-50 transition-colors"
                    >
                      Learn More
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-cyan-600 text-sm font-semibold uppercase tracking-widest">Why MARVY</span>
            <h2 className="text-2xl font-bold text-[#0c1b4d] mt-2">Why Work With Our Recruiters?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: 'Top-Tier Clients', desc: 'We place consultants with Fortune 500s, growing startups, and government agencies — roles that advance your career.' },
              { title: 'Competitive Rates', desc: 'We advocate for your market value. Our transparent process ensures you\'re compensated fairly for your expertise.' },
              { title: 'Ongoing Support', desc: 'Our relationship doesn\'t end at placement. We check in regularly and help you navigate contract renewals and transitions.' },
            ].map((item, i) => (
              <div key={i} className="p-6 rounded-2xl bg-slate-50 border border-gray-100">
                <FaCheckCircle className="text-cyan-500 mb-3" size={20} />
                <h3 className="font-bold text-[#0c1b4d] mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <p className="text-gray-500 mb-4">Don't see the right role? Send us your resume and we'll reach out when a match comes up.</p>
            <a
              href="mailto:hr@marvyitservices.com"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#0c1b4d] text-white font-semibold rounded-xl hover:bg-blue-900 transition-colors"
            >
              Submit Your Resume <FaArrowRight size={13} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
